import React from "react";
import { Box, Typography, Chip, Card, CardContent, Tooltip } from "@mui/material";
import {
  Email as EmailIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useOutlookPlugin } from "../../../hooks/useOutlookPlugin";
import type {
  OutlookShipmentFeed,
  OutlookShipmentFeedEvent,
} from "@services/demo/msoffice/types";
import { ShipmentDetails } from "./ShipmentDetails";

// Helper function to render email addresses with overflow handling
const renderEmailAddresses = (addresses: string[], label: string) => {
  if (!addresses || addresses.length === 0) return null;

  if (addresses.length === 1) {
    return (
      <Typography
        variant="caption"
        sx={{
          fontSize: "0.65rem",
          color: "text.disabled",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        <strong>{label}:</strong> {addresses[0]}
      </Typography>
    );
  }

  const firstAddress = addresses[0];
  const remainingCount = addresses.length - 1;
  const allAddresses = addresses.join(", ");

  return (
    <Typography
      variant="caption"
      sx={{
        fontSize: "0.65rem",
        color: "text.disabled",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      <strong>{label}:</strong> {firstAddress}{" "}
      <Tooltip title={allAddresses} arrow>
        <span style={{ textDecoration: "underline", cursor: "pointer" }}>
          (+{remainingCount} other{remainingCount > 1 ? "s" : ""})
        </span>
      </Tooltip>
    </Typography>
  );
};

interface ShipmentTimelineComponentProps {
  shipment: OutlookShipmentFeed;
}

export const ShipmentTimelineComponent: React.FC<
  ShipmentTimelineComponentProps
> = ({ shipment }) => {
  const { actions } = useOutlookPlugin();

  const handleOpenEmail = async (eventMessageId: string, eventName: string) => {
    try {
      await actions.openEmail(eventMessageId, eventName);
    } catch (error) {
      console.error("Failed to open email:", error);
    }
  };

  const handleAttachmentClick = (filename: string, url: string) => {
    // For demo purposes, we'll just log or alert
    console.log(`Opening attachment: ${filename} at ${url}`);
    alert(`Would open attachment: ${filename}`);
  };

  return (
    <Box>
      <Card sx={{ borderRadius: 0 }}>
        <CardContent>
          <ShipmentDetails
            shipment={{
              id: shipment.id,
              properties: shipment.properties,
            } as any}
            maxVisibleDetails={4}
          />

          {/* Timeline */}
          <Box position="relative">
            {/* Vertical line */}
            <Box
              sx={{
                position: "absolute",
                left: 31,
                top: 16,
                bottom: 16,
                width: 2,
                bgcolor: "primary.main",
                zIndex: 0,
              }}
            />

            {/* Timeline events */}
            {shipment.timeline.map((event, index) => (
              <TimelineEvent
                key={index}
                event={event}
                onOpenEmail={handleOpenEmail}
                onAttachmentClick={handleAttachmentClick}
                isLast={index === shipment.timeline.length - 1}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

interface TimelineEventProps {
  event: OutlookShipmentFeedEvent;
  onOpenEmail: (messageId: string, eventName: string) => void;
  onAttachmentClick: (filename: string, url: string) => void;
  isLast: boolean;
}

const TimelineEvent: React.FC<TimelineEventProps> = ({
  event,
  onOpenEmail,
  onAttachmentClick,
  isLast,
}) => {
  const eventDate = new Date(event.date);

  return (
    <Box
      sx={{
        position: "relative",
        pl: "50px",
        pb: isLast ? 0 : 3,
        minHeight: isLast ? "auto" : 80,
        "&:hover .email-icon": {
          opacity: 1,
        },
      }}
    >
      {/* Date on the left side of circle */}
      <Typography
        variant="caption"
        color="primary.main"
        sx={{
          position: "absolute",
          left: -20,
          top: 6,
          fontSize: "0.65rem",
          fontWeight: 500,
          textAlign: "right",
          width: 40,
        }}
      >
        {format(eventDate, "MMM d")}
      </Typography>

      {/* Timeline circle */}
      <Box
        sx={{
          position: "absolute",
          left: 24,
          top: 8,
          width: 16,
          height: 16,
          borderRadius: "50%",
          bgcolor: "primary.main",
          zIndex: 1,
          border: "3px solid white",
          boxShadow: 1,
        }}
      />

      {/* Hover Email Icon - positioned in top right of timeline item */}
      {event.messageId && (
        <Box
          className="email-icon"
          onClick={() => onOpenEmail(event.messageId!, event.name)}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            opacity: 0,
            transition: "opacity 0.2s ease",
            cursor: "pointer",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            borderRadius: "50%",
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: 1,
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.1)",
            },
          }}
        >
          <EmailIcon
            sx={{
              fontSize: 14,
              color: "primary.main",
            }}
          />
        </Box>
      )}

      {/* Event content */}
      <Box sx={{ pt: 0.5 }}>
        <Typography
          variant="body2"
          fontWeight="bold"
          color="primary"
          sx={{ mb: 0.5 }}
        >
          {event.name}
        </Typography>

        {/* From/To email addresses */}
        {event.emailFrom && event.emailFrom.length > 0 && (
          <Box sx={{ mb: 0 }}>
            {renderEmailAddresses(event.emailFrom, "From")}
          </Box>
        )}

        {event.emailTo && event.emailTo.length > 0 && (
          <Box sx={{ mb: 0.5 }}>
            {renderEmailAddresses(event.emailTo, "To")}
          </Box>
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, whiteSpace: "pre-line" }}
        >
          {event.description}
        </Typography>

        {/* Attachments */}
        {event.attachments.length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {event.attachments.map((attachment, index) => (
              <Chip
                key={index}
                label={attachment.filename}
                size="small"
                icon={<AttachFileIcon />}
                onClick={() =>
                  onAttachmentClick(attachment.filename, attachment.url)
                }
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  "& .MuiChip-icon": {
                    fontSize: "0.8rem",
                  },
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
